'use client';

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { ProductDetail } from '@/widgets/ProductDetail';

import { getDataFromStorage } from '@/utilities/dataStorage';
import { isBatteryProduct } from '@/utilities/getProductBaseCategory';
import { mapBatteryProductToOffer } from '@/utilities/mapBatteryProductToOffer';

import { useCityStore } from '@/stores/city/useCityStore';

import { batteryHeights, batteryPoles, CAR_BATTERY_VEHICLE_TYPE_ID, MOTOR_BATTERY_VEHICLE_TYPE_ID } from '@/constants/batteryProduct';
import { TEHRAN_CITY_ID } from '@/constants/common';

import { ProductDetailHydratedProps } from './models';

import { getProductAttributeByUuidService, getProductByUuidService } from '@/client/services/core/product/methods';
import { getProductBatteryByUuidService } from '@/client/services/core/productBattery/methods';
import { getOffersByProductUuidService } from '@/client/services/vendor/methods';
import { ProductAttribute, ProductType } from '@/server/services/core/product/models';
import { ProductBatteryType } from '@/server/services/core/productBattery/models';
import { ActiveOfferContent } from '@/server/services/vendor/models';
import { TickCircle } from 'iconsax-react';

const isBatteryVehicleType = (type: string) => type === CAR_BATTERY_VEHICLE_TYPE_ID || type === MOTOR_BATTERY_VEHICLE_TYPE_ID;

const shouldUseBatteryProductApi = (vehicleType: string, product: ProductType & Partial<ProductBatteryType>) =>
    isBatteryVehicleType(vehicleType) || isBatteryProduct(product);

const EMPTY_OFFER = {} as ActiveOfferContent;

const getStoredCity = () => {
    try {
        return JSON.parse(getDataFromStorage('userCity', 'local'));
    } catch {
        return null;
    }
};

export const ProductDetailHydrated = ({
    productUuid,
    initialProductDetail,
    initialCityId = TEHRAN_CITY_ID,
    vehicleType,
    allStores,
    winner,
    faqItems,
    preferredStoreUuid
}: ProductDetailHydratedProps) => {
    const cityId = useCityStore((state) => state.cityId);
    const cityLocation = useCityStore((state) => state.cityLocation);

    const [productData, setProductData] = useState<ProductType & Partial<ProductBatteryType>>(initialProductDetail);
    const [offerWinner, setOfferWinner] = useState<ActiveOfferContent>(winner?.offerId ? winner : EMPTY_OFFER);
    const [offerAllStores, setOfferAllStores] = useState<ActiveOfferContent[]>(allStores);
    const [productAttribute, setProductAttribute] = useState<ProductAttribute[]>([]);
    const [isAttributesLoading, setIsAttributesLoading] = useState<boolean>(true);
    const [isProductLoading, setIsProductLoading] = useState(false);
    const requestIdRef = useRef(0);
    const loadedCityIdRef = useRef(initialCityId);
    const loadedCityLocationRef = useRef(cityLocation);

    useLayoutEffect(() => {
        const storedCity = getStoredCity();
        if (storedCity?.id && storedCity.id !== initialCityId) {
            setIsProductLoading(true);
        }
    }, [initialCityId]);

    const handleGetProductDetails = async (nextCityId: number, nextCityLocation: [number, number] = cityLocation) => {
        const requestId = ++requestIdRef.current;
        setIsProductLoading(true);
        const useBatteryProductApi = shouldUseBatteryProductApi(vehicleType, productData);

        try {
            if (useBatteryProductApi) {
                const response = await getProductBatteryByUuidService(productUuid, {
                    cityId: nextCityId
                });
                if (requestId !== requestIdRef.current) return;
                setProductData(response.data.content);
            } else {
                const [productResponse, offersResponse] = await Promise.all([
                    getProductByUuidService(productUuid, {
                        cityId: nextCityId
                    }),
                    getOffersByProductUuidService(productUuid, {
                        customerCityId: nextCityId,
                        customerLat: nextCityLocation[0],
                        customerLng: nextCityLocation[1]
                    })
                ]);

                if (requestId !== requestIdRef.current) return;

                setProductData(productResponse.data.content);
                setOfferWinner(offersResponse.data.content?.winner || EMPTY_OFFER);
                setOfferAllStores(offersResponse.data.content?.allStores || []);
            }

            loadedCityIdRef.current = nextCityId;
            loadedCityLocationRef.current = nextCityLocation;
        } catch {
            if (requestId === requestIdRef.current && !useBatteryProductApi) {
                setOfferWinner(EMPTY_OFFER);
                setOfferAllStores([]);
            }
        } finally {
            if (requestId === requestIdRef.current) {
                setIsProductLoading(false);
            }
        }
    };

    const handleGetProductsAttribute = async () => {
        if (!productUuid) return;
        setIsAttributesLoading(true);
        try {
            const response = await getProductAttributeByUuidService(productUuid);
            setProductAttribute(response.data.content);
        } catch (error) {
            console.error(error);
        } finally {
            setIsAttributesLoading(false);
        }
    };

    useEffect(() => {
        void handleGetProductsAttribute();
    }, [productUuid]);

    useEffect(() => {
        const storedCity = getStoredCity();
        const effectiveCityId = storedCity?.id || cityId;
        const effectiveCityLocation: [number, number] =
            typeof storedCity?.latitude === 'number' && typeof storedCity?.longitude === 'number'
                ? [storedCity.latitude, storedCity.longitude]
                : cityLocation;

        if (
            effectiveCityId === loadedCityIdRef.current &&
            effectiveCityLocation[0] === loadedCityLocationRef.current[0] &&
            effectiveCityLocation[1] === loadedCityLocationRef.current[1]
        ) {
            return;
        }

        if (cityId === TEHRAN_CITY_ID && effectiveCityId !== TEHRAN_CITY_ID && cityId !== effectiveCityId) {
            return;
        }

        void handleGetProductDetails(effectiveCityId, effectiveCityLocation);
    }, [cityId, cityLocation]);

    useEffect(() => {
        setOfferWinner(winner?.offerId != null ? winner : EMPTY_OFFER);
        setOfferAllStores(allStores);
        loadedCityIdRef.current = initialCityId;
    }, [winner, allStores, initialCityId]);

    const { resolvedWinner, resolvedAllStores } = useMemo(() => {
        if (shouldUseBatteryProductApi(vehicleType, productData)) {
            const batteryOffer = mapBatteryProductToOffer(productData);
            return { resolvedWinner: batteryOffer, resolvedAllStores: [batteryOffer] };
        }

        return { resolvedWinner: offerWinner, resolvedAllStores: offerAllStores };
    }, [vehicleType, productData, offerWinner, offerAllStores]);

    return (
        !!productData && (
            <ProductDetail
                isPriceLoading={isProductLoading}
                allStores={resolvedAllStores}
                winner={resolvedWinner}
                guarantee={productData?.guarantee || null}
                vehicleType={vehicleType}
                title={productData.title}
                discount={productData.discountPercentage}
                sellingPrice={productData.sellingPrice}
                slug={productData.slug}
                subtitle={productData.subTitle}
                description={productData.description}
                attributes={productAttribute}
                price={productData.price}
                uuid={productData.uuid}
                badges={[
                    ...(productData.available && (productData.brand.name.includes('تینو') || productData.brand.name.includes('پلاس'))
                        ? [
                              {
                                  color: 'primary' as const,
                                  title: 'پیشنهاد ازکی‌سرویس',
                                  icon: <TickCircle size={14} color='currentcolor' />
                              }
                          ]
                        : []),
                    ...(productData?.height
                        ? [
                              {
                                  title: batteryHeights[productData.height],
                                  color: 'secondary' as const
                              }
                          ]
                        : []),
                    ...(productData.pole
                        ? [
                              {
                                  title: batteryPoles[productData.pole],
                                  color: 'success' as const
                              }
                          ]
                        : [])
                ]}
                category={productData.category}
                seoDescription={productData.seoMetadata?.seoDescription || ''}
                brand={productData.brand}
                available={productData.available}
                ampere={productData.ampere}
                pole={productData.pole}
                height={productData.height}
                faqItems={faqItems}
                preferredStoreUuid={preferredStoreUuid}
                isAttributesLoading={isAttributesLoading}
            />
        )
    );
};
